import { Divider } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import Paper from '@material-ui/core/Paper';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import axios from 'axios';
import { observer } from 'mobx-react-lite';
import * as qs from 'qs';
import React, { useContext, useEffect, useState } from 'react';
import { Link, LinkProps, RouteComponentProps, withRouter } from 'react-router-dom';
import WindsorApi from '../../api/WindsorApi';
import UserContext from '../../auth/UserContext';
import LoadingIndicator from '../../common/LoadingIndicator';
import NoResultsText from '../../common/NoResultsText';
import MobxContext from '../../mobx/MobxContext';
import FaroDecksTable from './FaroDecksTable';

const CancelToken = axios.CancelToken;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: 1200,
      margin: 'auto'
    },
    divider: {
      marginBottom: 16,
      marginTop: 8
    },
    searchTextField: {
      backgroundColor: theme.palette.background.paper,
      marginBottom: 16
    },
    recentlyViewedSection: {
      marginBottom: 24
    },
    chip: {
      color: theme.palette.text.secondary,
      margin: '0 8px 8px 0'
    },
    chipContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      paddingTop: 8
    },
    landingPageSearchContainer: { 
      maxWidth: 600, 
      margin: '64px auto auto auto' 
    },
    landingPageTitle: { 
      marginBottom: 24 
    },
    quickLinksContainer: { 
      marginTop: 24 
    }
  })
);

const AdapterLink = React.forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => (
  <Link innerRef={ref as any} {...props} />
));

function transformDeck(item) {
  return {
    deckName: item.DeckName,
    version: item.Version,
    buildDate: item.BuildAttributes && item.BuildAttributes['faro.build.timestamp'] ? item.BuildAttributes['faro.build.timestamp'] : null,
    tags: item.Tags || []
  };
}

const windsorApi = new WindsorApi();

function FaroDeckSearchPage(props: RouteComponentProps<any>) {
  const { history, location } = props;
  const classes = useStyles({});
  const { search = '' } = qs.parse(location.search, { ignoreQueryPrefix: true });
  const [searchResults, setSearchResults] = useState(null);
  const [decksAuthoredByUser, setDecksAuthoredByUser] = useState(null);
  const [searchInProgress, setSearchInProgress] = useState(false);
  const [page, setPage] = useState(0);
  const { preferenceStore } = useContext(MobxContext);
  const user = useContext(UserContext);

  useEffect(() => {
    setSearchInProgress(false);
    setSearchResults(null);

    if (search) {
      setSearchInProgress(true);

      const cancelTokenSource = CancelToken.source();
      windsorApi.getFaroDecks(search, undefined, { cancelToken: cancelTokenSource.token }).then(res => {
        setSearchResults(res.map(transformDeck));
        setSearchInProgress(false);
      }).catch(error => {
        if (!axios.isCancel(error)) {
          setSearchInProgress(false);
          console.error(`Failed to load decks:\n${error}`);
        }
      });

      return () => cancelTokenSource.cancel();
    }
  }, [search]);

  useEffect(() => {
    const cancelTokenSource = CancelToken.source();
    windsorApi.getFaroDecks(
      '',
      { attributes: `git.author.email=${user.email}` },
      { cancelToken: cancelTokenSource.token }).then(res => {
        const decks = res.map(transformDeck);
        decks.sort((a, b) => {
          return b.buildDate.localeCompare(a.buildDate);
        });
        setDecksAuthoredByUser(decks.slice(0, 10));
      }).catch(error => {
        if (!axios.isCancel(error)) {
          console.error(`Failed to load decks:\n${error}`);
        }
      });
    return () => cancelTokenSource.cancel();
  }, [user.email]);

  const updateQueryParams = (newParams) => {
    let queryString = '';
    Object.keys(newParams).filter(key => newParams[key] !== undefined && newParams[key] !== '').forEach(key => {
      if (queryString.length) {
        queryString += '&';
      }
      queryString += `${key}=${newParams[key]}`;
    });
    if (queryString.length) {
      queryString = `?${queryString}`;
    }
    history.push({ search: queryString })
  };

  function onSearchChange(newSearch) {
    setPage(0);
    updateQueryParams({ search: newSearch });
  }

  const onSearchInputKeyPress = (event) => {
    // Only handle enter key type
    if (event.which === 13) {
      onSearchChange(event.target.value);
    }
  }

  function onPageChange(newPage: number) {
    setPage(newPage);
  }

  const useLandingPageFormat = !searchResults && !searchInProgress;

  return (
    <div className={classes.root}>
      <div>
        {!useLandingPageFormat && <div>
          <Typography variant="h5" gutterBottom>Decks</Typography>
          <Divider className={classes.divider} light />
        </div>}
        <div className={useLandingPageFormat ? classes.landingPageSearchContainer : null}>
          <div>
            {useLandingPageFormat && <Typography variant="h5" className={classes.landingPageTitle}>Search for Decks</Typography>}
            <TextField
              className={classes.searchTextField}
              id="deck-search"
              size={useLandingPageFormat ? 'medium' : 'small'}
              defaultValue={search}
              fullWidth
              autoComplete="off"
              margin="none"
              variant="outlined"
              placeholder="Search by deck name"
              inputProps={{ 'aria-label': 'bare', spellCheck: false }}
              onKeyPress={onSearchInputKeyPress}
            />
          </div>
          {!searchResults && !searchInProgress && decksAuthoredByUser && <div className={classes.quickLinksContainer}>
            {preferenceStore.recentlyViewedDecks.length > 0 && <div className={classes.recentlyViewedSection}>
              <Typography variant="subtitle2" color="textSecondary">RECENTLY VIEWED</Typography>
              <div className={classes.chipContainer}>
                {preferenceStore.recentlyViewedDecks.map(deck => <Chip
                  key={deck}
                  className={classes.chip}
                  size="small"
                  component={AdapterLink}
                  to={`/faro/decks/${deck}`}
                  clickable
                  variant="outlined"
                  label={deck}
                />)}
              </div>
            </div>}
            {decksAuthoredByUser.length > 0 && <div>
              <Typography variant="subtitle2" color="textSecondary">RECENTLY UPDATED</Typography>
              <div className={classes.chipContainer}>
                {decksAuthoredByUser.map(deck => <Chip
                  key={deck.deckName}
                  className={classes.chip}
                  size="small"
                  component={AdapterLink}
                  to={`/faro/decks/${deck.deckName}?version=${encodeURIComponent(deck.version)}`}
                  clickable
                  variant="outlined"
                  label={deck.deckName}
                />)}
              </div>
            </div>}
          </div>}
          {searchResults && searchResults.length > 0 && <div>
            <Typography color="textSecondary">{searchResults.length} deck{searchResults.length !== 1 ? 's' : ''} found</Typography>
            <Paper>
              <FaroDecksTable
                decks={searchResults}
                page={page}
                onPageChange={onPageChange}
              />
            </Paper>
          </div>}
          {search && searchResults && searchResults.length === 0 && <NoResultsText>No decks matching search</NoResultsText>}
          <LoadingIndicator loading={searchInProgress || !decksAuthoredByUser} />
        </div>
      </div>
    </div>
  );
};

export default withRouter(observer(FaroDeckSearchPage));
